import React, { useState, useEffect } from 'react';
import { Mail, MapPin, Play, Check, Linkedin, Twitter, Github, Terminal, Sparkles, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { personalInfo } from '../mock';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isRunning, setIsRunning] = useState(false);
  const [showOutput, setShowOutput] = useState(false);
  const [codePreview, setCodePreview] = useState('');
  const [outputData, setOutputData] = useState(null);
  const [error, setError] = useState(null);
  const { theme } = useTheme();

  // Generate code preview as user types
  useEffect(() => {
    const code = `# Contact Request
contact_data = {
    'from': '${formData.name || '[Your Name]'}',
    'email': '${formData.email || '[Your Email]'}',
    'message': '''${formData.message ? formData.message.substring(0, 30) + '...' : '[Your Message]'}'''
}

send_message(to='murtaza@kanchwala.in')`;
    setCodePreview(code);
  }, [formData]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setShowOutput(false);
    setError(null);
  };

  const handleRunCell = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!formData.name || !formData.email || !formData.message) {
      return;
    }

    setIsRunning(true);
    setShowOutput(false);
    setError(null);

    try {
      // Make API call to backend
      const response = await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        subject: formData.subject || undefined,
        message: formData.message
      });

      // Simulate the running animation for better UX
      setTimeout(() => {
        setIsRunning(false);
        setShowOutput(true);
        setOutputData(response.data);
        
        // Reset form after 5 seconds
        setTimeout(() => {
          setFormData({ name: '', email: '', subject: '', message: '' });
          setTimeout(() => {
            setShowOutput(false);
            setOutputData(null);
          }, 500);
        }, 5000);
      }, 1500);

    } catch (err) {
      setIsRunning(false);
      setShowOutput(true);
      
      // Extract error message from different error formats
      let errorMessage = 'Failed to send message';
      
      if (err.response?.data) {
        const data = err.response.data;
        
        // Handle FastAPI validation errors (array of objects)
        if (Array.isArray(data.detail)) {
          errorMessage = data.detail.map(e => `${e.loc.join('.')}: ${e.msg}`).join(', ');
        }
        // Handle simple error messages
        else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        }
        // Handle other error formats
        else if (data.message) {
          errorMessage = data.message;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      console.error('Error sending message:', err);
    }
  };

  return (
    <section id="contact" className={`py-12 sm:py-20 px-4 sm:px-6 lg:px-8 ${
      theme === 'dark' 
        ? 'bg-gradient-to-br from-[#1B3139] to-[#0f1f26]' 
        : 'bg-gradient-to-br from-gray-100 to-white'
    }`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <div className="inline-flex items-center space-x-2 mb-4">
            <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-[#FF3621]" />
            <span 
              className="text-[#FF3621] font-semibold text-xs sm:text-sm uppercase tracking-wider"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Get in Touch
            </span>
          </div>
          <h2 
            className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Run Your Message
          </h2>
          <p 
            className={`text-base sm:text-lg max-w-2xl mx-auto px-4 ${
              theme === 'dark' ? 'text-white/60' : 'text-gray-600'
            }`}
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Write your message in the notebook cell below and execute to send
          </p>
        </div>

        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Main Notebook Cell - Contact Form */}
          <div className="lg:col-span-2">
            <div className={`border rounded-lg overflow-hidden shadow-2xl ${
              theme === 'dark' 
                ? 'bg-[#1B3139] border-white/10' 
                : 'bg-white border-gray-200'
            }`}>
              {/* Cell Header */}
              <div className={`flex items-center justify-between px-3 sm:px-4 py-2 border-b ${
                theme === 'dark' 
                  ? 'bg-white/5 border-white/10' 
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center space-x-1.5 sm:space-x-2">
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-500"></div>
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-green-500"></div>
                </div>
                <span 
                  className={`text-xs font-mono ${
                    theme === 'dark' ? 'text-white/40' : 'text-gray-500'
                  }`}
                  style={{ fontFamily: 'DM Mono, monospace' }}
                >
                  Cell [99]: send_message()
                </span>
              </div>

              {/* Input Section */}
              <div className="relative">
                <div className={`absolute left-0 top-0 bottom-0 w-10 sm:w-12 border-r flex items-start justify-center pt-4 sm:pt-6 ${
                  theme === 'dark' 
                    ? 'bg-white/5 border-white/10' 
                    : 'bg-gray-50 border-gray-200'
                }`}>
                  <Button
                    onClick={handleRunCell}
                    disabled={isRunning || !formData.name || !formData.email || !formData.message}
                    className="bg-transparent hover:bg-[#FF3621]/20 border border-[#FF3621]/50 text-[#FF3621] p-1.5 sm:p-2 h-7 w-7 sm:h-8 sm:w-8 rounded transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    {isRunning ? (
                      <div className="animate-spin h-3 w-3 sm:h-4 sm:w-4 border-2 border-[#FF3621] border-t-transparent rounded-full"></div>
                    ) : showOutput ? (
                      <Check className="h-3 w-3 sm:h-4 sm:w-4" />
                    ) : (
                      <Play className="h-3 w-3 sm:h-4 sm:w-4" />
                    )}
                  </Button>
                </div>

                <form onSubmit={handleRunCell} className="pl-12 sm:pl-16 pr-4 sm:pr-6 py-4 sm:py-6 space-y-3 sm:space-y-4">
                  {/* Code Preview */}
                  <div className="mb-4 sm:mb-6">
                    <pre 
                      className={`text-xs font-mono mb-4 overflow-x-auto whitespace-pre-wrap sm:whitespace-pre ${
                        theme === 'dark' ? 'text-white/50' : 'text-gray-500'
                      }`}
                      style={{ fontFamily: 'DM Mono, monospace' }}
                    >
                      {codePreview}
                    </pre>
                  </div>

                  {/* Input Fields styled as code comments */}
                  <div>
                    <label 
                      className="text-xs text-[#FF3621] font-mono mb-2 block"
                      style={{ fontFamily: 'DM Mono, monospace' }}
                    >
                      # Your Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Enter your name"
                      className={`w-full border rounded px-3 sm:px-4 py-2.5 sm:py-3 font-mono text-sm focus:outline-none focus:border-[#FF3621] focus:ring-1 focus:ring-[#FF3621] transition-all ${
                        theme === 'dark' 
                          ? 'bg-black/30 border-white/20 text-white placeholder-white/40' 
                          : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'
                      }`}
                      style={{ fontFamily: 'DM Mono, monospace' }}
                    />
                  </div>

                  <div>
                    <label 
                      className="text-xs text-[#FF3621] font-mono mb-2 block"
                      style={{ fontFamily: 'DM Mono, monospace' }}
                    >
                      # Your Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your.email@example.com"
                      className={`w-full border rounded px-3 sm:px-4 py-2.5 sm:py-3 font-mono text-sm focus:outline-none focus:border-[#FF3621] focus:ring-1 focus:ring-[#FF3621] transition-all ${
                        theme === 'dark' 
                          ? 'bg-black/30 border-white/20 text-white placeholder-white/40' 
                          : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'
                      }`}
                      style={{ fontFamily: 'DM Mono, monospace' }}
                    />
                  </div>

                  <div>
                    <label 
                      className="text-xs text-[#FF3621] font-mono mb-2 block"
                      style={{ fontFamily: 'DM Mono, monospace' }}
                    >
                      # Subject (Optional)
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="What's this about?"
                      className={`w-full border rounded px-3 sm:px-4 py-2.5 sm:py-3 font-mono text-sm focus:outline-none focus:border-[#FF3621] focus:ring-1 focus:ring-[#FF3621] transition-all ${
                        theme === 'dark' 
                          ? 'bg-black/30 border-white/20 text-white placeholder-white/40' 
                          : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'
                      }`}
                      style={{ fontFamily: 'DM Mono, monospace' }}
                    />
                  </div>

                  <div>
                    <label 
                      className="text-xs text-[#FF3621] font-mono mb-2 block"
                      style={{ fontFamily: 'DM Mono, monospace' }}
                    >
                      # Message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      placeholder="Tell me about your project or inquiry..."
                      className={`w-full border rounded px-3 sm:px-4 py-2.5 sm:py-3 font-mono text-sm focus:outline-none focus:border-[#FF3621] focus:ring-1 focus:ring-[#FF3621] resize-none transition-all ${
                        theme === 'dark' 
                          ? 'bg-black/30 border-white/20 text-white placeholder-white/40' 
                          : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'
                      }`}
                      style={{ fontFamily: 'DM Mono, monospace' }}
                    />
                  </div>

                  <div className="flex items-center space-x-2 pt-2">
                    <Terminal className={`h-4 w-4 ${theme === 'dark' ? 'text-white/40' : 'text-gray-400'}`} />
                    <span 
                      className={`text-xs font-mono ${theme === 'dark' ? 'text-white/40' : 'text-gray-500'}`}
                      style={{ fontFamily: 'DM Mono, monospace' }}
                    >
                      Click run to execute
                    </span>
                  </div>
                </form>
              </div>

              {/* Output Section */}
              {showOutput && !error && outputData && (
                <div className={`border-t ${
                  theme === 'dark' 
                    ? 'border-white/10 bg-black/30' 
                    : 'border-gray-200 bg-gray-50'
                }`}>
                  <div className={`px-4 py-2 border-b ${
                    theme === 'dark' 
                      ? 'bg-white/5 border-white/10' 
                      : 'bg-gray-100 border-gray-200'
                  }`}>
                    <span 
                      className={`text-xs font-mono ${theme === 'dark' ? 'text-white/40' : 'text-gray-500'}`}
                      style={{ fontFamily: 'DM Mono, monospace' }}
                    >
                      Output:
                    </span>
                  </div>
                  <div className="px-6 py-6">
                    <div className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-1" />
                      <div>
                        <pre 
                          className="text-sm text-green-500 font-mono mb-2"
                          style={{ fontFamily: 'DM Mono, monospace' }}
                        >
                          ✓ Message sent successfully!
                        </pre>
                        <p 
                          className={`text-sm font-mono ${theme === 'dark' ? 'text-white/60' : 'text-gray-600'}`}
                          style={{ fontFamily: 'DM Mono, monospace' }}
                        >
                          Your message has been delivered to {outputData.recipient}<br />
                          Message ID: <span className="text-blue-500">{outputData.id.substring(0, 8)}...</span><br />
                          Timestamp: <span className="text-blue-500">{new Date(outputData.timestamp).toLocaleString()}</span><br />
                          Status: <span className="text-green-500">DELIVERED</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Output Section */}
              {showOutput && error && (
                <div className={`border-t ${
                  theme === 'dark'
                    ? 'border-white/10 bg-black/30'
                    : 'border-gray-200 bg-gray-50'
                }`}>
                  <div className={`px-4 py-2 border-b ${
                    theme === 'dark'
                      ? 'bg-white/5 border-white/10'
                      : 'bg-gray-100 border-gray-200'
                  }`}>
                    <span 
                      className={`text-xs font-mono ${theme === 'dark' ? 'text-white/40' : 'text-gray-500'}`}
                      style={{ fontFamily: 'DM Mono, monospace' }}
                    >
                      Error:
                    </span>
                  </div>
                  <div className="px-6 py-6">
                    <div className="flex items-start space-x-3">
                      <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-1" />
                      <div>
                        <pre 
                          className="text-sm text-red-400 font-mono mb-2"
                          style={{ fontFamily: 'DM Mono, monospace' }}
                        >
                          ✗ Error sending message
                        </pre>
                        <p 
                          className={`text-sm font-mono ${theme === 'dark' ? 'text-white/60' : 'text-gray-600'}`}
                          style={{ fontFamily: 'DM Mono, monospace' }}
                        >
                          {String(error)}<br />
                          <span className="text-red-400">Please check your input and try again.</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className={`mt-4 flex items-center space-x-2 text-xs ${
              theme === 'dark' ? 'text-white/40' : 'text-gray-500'
            }`}>
              <Sparkles className="h-4 w-4" />
              <span style={{ fontFamily: 'DM Sans, sans-serif' }}>
                Tip: Fill in all required fields to enable the run button
              </span>
            </div>
          </div>

          {/* Sidebar - Contact Info */}
          <div className="space-y-6">
            {/* Contact Information Card */}
            <div className={`border rounded-lg p-6 shadow-xl ${
              theme === 'dark'
                ? 'bg-[#1B3139] border-white/10'
                : 'bg-white border-gray-200'
            }`}>
              <h3 
                className={`text-lg font-bold mb-4 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                Direct Contact
              </h3>

              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-[#FF3621]/10 border border-[#FF3621]/30 rounded">
                    <Mail className="h-4 w-4 text-[#FF3621]" />
                  </div>
                  <div>
                    <p 
                      className={`text-xs mb-1 ${theme === 'dark' ? 'text-white/60' : 'text-gray-500'}`}
                      style={{ fontFamily: 'DM Sans, sans-serif' }}
                    >
                      Email
                    </p>
                    <a
                      href={`mailto:${personalInfo.email}`}
                      className={`text-sm font-medium hover:text-[#FF3621] transition-colors ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}
                      style={{ fontFamily: 'DM Mono, monospace' }}
                    >
                      {personalInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-[#FF3621]/10 border border-[#FF3621]/30 rounded">
                    <MapPin className="h-4 w-4 text-[#FF3621]" />
                  </div>
                  <div>
                    <p 
                      className={`text-xs mb-1 ${theme === 'dark' ? 'text-white/60' : 'text-gray-500'}`}
                      style={{ fontFamily: 'DM Sans, sans-serif' }}
                    >
                      Location
                    </p>
                    <p 
                      className={`text-sm font-medium ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}
                      style={{ fontFamily: 'DM Sans, sans-serif' }}
                    >
                      {personalInfo.location}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Links Card */}
            <div className={`border rounded-lg p-6 shadow-xl ${
              theme === 'dark'
                ? 'bg-[#1B3139] border-white/10'
                : 'bg-white border-gray-200'
            }`}>
              <h3 
                className={`text-lg font-bold mb-4 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                Connect
              </h3>

              <div className="space-y-3">
                <a
                  href={personalInfo.social.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center space-x-3 p-3 hover:bg-[#FF3621]/20 hover:border-[#FF3621]/50 border rounded-lg transition-all duration-300 group ${
                    theme === 'dark' ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <Linkedin className={`h-5 w-5 group-hover:text-[#FF3621] transition-colors ${
                    theme === 'dark' ? 'text-white/60' : 'text-gray-600'
                  }`} />
                  <span 
                    className={`text-sm font-medium ${
                      theme === 'dark' ? 'text-white/80 group-hover:text-white' : 'text-gray-700 group-hover:text-gray-900'
                    }`}
                    style={{ fontFamily: 'DM Sans, sans-serif' }}
                  >
                    LinkedIn
                  </span>
                </a>

                <a
                  href={personalInfo.social.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center space-x-3 p-3 hover:bg-[#FF3621]/20 hover:border-[#FF3621]/50 border rounded-lg transition-all duration-300 group ${
                    theme === 'dark' ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <Twitter className={`h-5 w-5 group-hover:text-[#FF3621] transition-colors ${
                    theme === 'dark' ? 'text-white/60' : 'text-gray-600'
                  }`} />
                  <span 
                    className={`text-sm font-medium ${
                      theme === 'dark' ? 'text-white/80 group-hover:text-white' : 'text-gray-700 group-hover:text-gray-900'
                    }`}
                    style={{ fontFamily: 'DM Sans, sans-serif' }}
                  >
                    Twitter
                  </span>
                </a>

                <a
                  href="https://github.com/mkanchwala"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center space-x-3 p-3 hover:bg-[#FF3621]/20 hover:border-[#FF3621]/50 border rounded-lg transition-all duration-300 group ${
                    theme === 'dark' ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <Github className={`h-5 w-5 group-hover:text-[#FF3621] transition-colors ${
                    theme === 'dark' ? 'text-white/60' : 'text-gray-600'
                  }`} />
                  <span 
                    className={`text-sm font-medium ${
                      theme === 'dark' ? 'text-white/80 group-hover:text-white' : 'text-gray-700 group-hover:text-gray-900'
                    }`}
                    style={{ fontFamily: 'DM Sans, sans-serif' }}
                  >
                    GitHub
                  </span>
                </a>
              </div>
            </div>

            {/* Response Time Info */}
            <div className="bg-gradient-to-br from-[#FF3621]/10 to-transparent border border-[#FF3621]/30 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <Sparkles className="h-5 w-5 text-[#FF3621] flex-shrink-0 mt-0.5" />
                <div>
                  <p 
                    className={`text-sm font-medium mb-1 ${
                      theme === 'dark' ? 'text-white/80' : 'text-gray-800'
                    }`}
                    style={{ fontFamily: 'DM Sans, sans-serif' }}
                  >
                    Quick Response
                  </p>
                  <p 
                    className={`text-xs ${
                      theme === 'dark' ? 'text-white/60' : 'text-gray-600'
                    }`}
                    style={{ fontFamily: 'DM Sans, sans-serif' }}
                  >
                    I typically respond within 24-48 hours. For urgent matters, connect via LinkedIn.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
